import type { DocNode } from '@atlaskit/adf-schema';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { type FireAnalyticEventCallback as ADFStreamerFireAnalyticEventCallback } from '@atlassian/ai-model-io/adf-streamer';
import type {
	EditorAIAnalyticEventPayload,
	ADFStreamingInfoAEP,
	ADFStreamingErrorAEP,
} from '@atlassian/editor-plugin-ai';

import {
	type API,
	type StreamingCommand,
	allStreamingCommands,
} from '../aiStreamingOrchestratorPluginType';

import { getPmDoc } from './utils/get-pm-doc';
import { isAttributeOnlyChange } from './utils/is-attribute-only-change';

export function createFireADFStreamingAnalyticsEvent({
	api,
	isCreateAction,
	traceId,
}: {
	api: API;
	isCreateAction: boolean;
	traceId: string | undefined;
}): ADFStreamerFireAnalyticEventCallback {
	return (event) => {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			const commonAttributes:
				| Omit<ADFStreamingErrorAEP['attributes'], 'errorMessage' | 'errorData'>
				| Omit<ADFStreamingInfoAEP['attributes'], 'message' | 'data'> = {
				traceId: fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
				experienceName: null,
				selectionType: null,
				selectionCategory: null,
				streamId: null,
				source: { type: 'orchestrator', isCreateAction },
			};
			if (event.type === 'error') {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							errorMessage: event.message,
							errorData: event.data,
						},
					},
				);
			} else {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							message: event.message,
							data: event.data,
						},
					},
				);
			}
		} else {
			if (event.type === 'error') {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							errorMessage: event.message,
							errorData: event.data,
							experienceName: null,
							selectionType: null,
							selectionCategory: null,
							streamId: null,
							source: { type: 'orchestrator', isCreateAction },
						},
					},
				);
			} else {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							message: event.message,
							data: event.data,
							experienceName: null,
							selectionType: null,
							selectionCategory: null,
							streamId: null,
							source: { type: 'orchestrator', isCreateAction },
						},
					},
				);
			}
		}
	};
}

type AttributeOnlyReplaceNodePayload = {
	action: 'replaceNodeAction';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'orchestratorReplaceNode';
	attributes: {
		attributeOnlyChange: boolean;
		nodeName: string | undefined;
	};
	eventType: EVENT_TYPE.TRACK;
};

export function fireAttributeOnlyReplaceNodeAnalytics({
	api,
	initialNode,
	lastDoc,
}: {
	api: API;
	initialNode: PMNode | undefined;
	lastDoc: DocNode | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<AttributeOnlyReplaceNodePayload, 'customEventType'>({
		action: 'replaceNodeAction',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'orchestratorReplaceNode',
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			attributeOnlyChange: isAttributeOnlyChange(api, initialNode, lastDoc),
			nodeName: initialNode?.type.name,
		},
	});
}

// Validates the command is valid ()
const validateCommandType = (cmd: StreamingCommand | undefined) =>
	cmd?.type && allStreamingCommands.includes(cmd.type) ? cmd.type : 'invalid';

export function checkAndFireNoDocumentChangeAnalytics({
	api,
	docBeforeStreaming,
	command,
}: {
	api: API;
	command: StreamingCommand | undefined;
	docBeforeStreaming: PMNode | undefined;
}): void {
	const docAfterStreaming = getPmDoc(api);
	if (docBeforeStreaming && docAfterStreaming) {
		const isSameDoc = docBeforeStreaming.eq(docAfterStreaming);
		const isSameDocIgnoreAttrs = areNodesEqualIgnoreAttrs(docBeforeStreaming, docAfterStreaming);
		if (isSameDoc || isSameDocIgnoreAttrs) {
			api.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.NO_DOC_CHANGE_FOUND,
				actionSubject: ACTION_SUBJECT.AI_STREAMING,
				actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					command: fg('platform_editor_ai_traceid_in_analytics_events')
						? validateCommandType(command)
						: '',
					isSameDoc,
					isSameDocIgnoreAttrs,
				},
			});
		}
	}
}

export function fireADFStreamingEndSessionErrorAnalytics({
	api,
	sessionStarted,
	endSessionCallCount,
	lastEndedSessionId,
	isDoubleCall,
}: {
	api: API;
	endSessionCallCount: number;
	isDoubleCall: boolean;
	lastEndedSessionId: string | null;
	sessionStarted: boolean;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreamingEndSession',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			sessionStarted,
			endSessionCallCount,
			lastEndedSessionId,
			isDoubleCall,
		},
	});
}

export function fireSessionStartedAnalytics({
	api,
	sessionId,
	traceId,
}: {
	api: API;
	sessionId: string;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'sessionStart',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'userFlow',
		eventType: EVENT_TYPE.UI,
		attributes: {
			aiGroupId: sessionId,
			actionTaken: undefined,
			editCount: 0,
			aiSessionId: '',
			traceIds: traceId ? [traceId] : undefined,
		},
	});
}
